import React, { useEffect, useState } from "react";
import { Brain, Check } from "lucide-react";

// Tracks elapsed ms from the rising edge of `isLoading` (user sent a message,
// agent is working) to the falling edge (turn finished). Returns both the
// live counter (`elapsedMs`) and the duration of the most-recently-completed
// turn (`lastDurationMs`) — the latter powers the "Took Xs" completion pill
// and persists until the next turn starts.
export function useTurnTimer(isLoading?: boolean): {
  elapsedMs: number | null;
  lastDurationMs: number | null;
} {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [lastDurationMs, setLastDurationMs] = useState<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      if (startedAt == null) {
        const t = Date.now();
        setStartedAt(t);
        setNow(t);
        setLastDurationMs(null);
      }
    } else if (startedAt != null) {
      setLastDurationMs(Math.max(0, Date.now() - startedAt));
      setStartedAt(null);
    }
  }, [isLoading, startedAt]);

  useEffect(() => {
    if (startedAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsedMs = startedAt === null ? null : Math.max(0, now - startedAt);
  return { elapsedMs, lastDurationMs };
}

export function formatTurnElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

// When `isCompacting` is true the label swaps to signal that the pause is
// Anthropic running a context-compaction edit (a summarization step triggered
// around 60K input tokens), not a hang. `elapsedMs` is the turn timer — shown
// inline so the user always has a concrete "how long has this taken" signal.
export function ThinkingBubble({
  name,
  isCompacting,
  elapsedMs,
}: {
  name: string;
  isCompacting?: boolean;
  elapsedMs?: number | null;
}) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-3 rounded-2xl rounded-bl-sm bg-card border border-border/50 shadow-sm w-fit max-w-[85%] text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-300">
      <Brain className="w-3.5 h-3.5 shrink-0 animate-pulse text-primary/60" />
      <span className={`font-medium ${isCompacting ? "italic" : ""}`}>
        {isCompacting ? "Compacting context…" : `${name} is thinking`}
      </span>
      <span className="flex gap-0.5">
        <span className="w-1 h-1 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1 h-1 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-1 h-1 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} />
      </span>
      {elapsedMs != null && (
        <span className="tabular-nums text-muted-foreground/60 font-medium">
          {formatTurnElapsed(elapsedMs)}
        </span>
      )}
    </div>
  );
}

// Replaces the thinking bubble once `isLoading` falls back to false, giving
// the user a concrete "Took 12s" signal. Stays visible until the next turn
// starts (at which point the rising edge clears it).
export function TurnCompletedPill({ ms }: { ms: number }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/30 border border-border/30 w-fit text-[10px] text-muted-foreground/80 animate-in fade-in duration-300">
      <Check className="w-3 h-3 shrink-0 text-primary/60" />
      <span className="font-medium tabular-nums">Took {formatTurnElapsed(ms)}</span>
    </div>
  );
}
